import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiClock, FiFileText, FiShield, FiDownload, FiX, FiCheck, FiZap } from 'react-icons/fi';
import logoKabupaten from '../assets/Lambang_Kabupaten_Bogor.png';
import useThemeColor from '../hooks/useThemeColor';

const LandingPage = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Set theme color for landing page
  useThemeColor('#0f766e');

  useEffect(() => {
    // Check if opened as PWA (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIosStandalone = window.navigator.standalone === true;
    
    // If opened as PWA, redirect to login
    if (isStandalone || isIosStandalone) {
      navigate('/login', { replace: true });
      return;
    }

    // Browser mode: setup install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [navigate]);

  const handleInstallClick = () => {
    if (!deferredPrompt) {
      setShowInstallModal(true);
      return;
    }
    
    installPWA();
  };

  const installPWA = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('✅ User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
  };

  const features = [
    { 
      icon: FiZap, 
      title: "Proses Cepat", 
      desc: "Pengajuan instan"
    },
    { 
      icon: FiShield, 
      title: "Aman & Terpercaya", 
      desc: "Data terenkripsi"
    },
    { 
      icon: FiClock, 
      title: "Akses 24/7", 
      desc: "Kapan saja & dimana saja"
    }
  ];

  return (
    <div className="relative h-screen overflow-hidden bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Subtle Pattern Overlay */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(20, 184, 166, 0.05) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)`
        }}></div>
      </div>

      {/* Animated Gradient Blobs - Subtle */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 -left-40 w-80 h-80 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
        <div className="absolute top-0 -right-40 w-80 h-80 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content - Centered & Compact */}
      <main className="relative z-10 h-full flex items-center justify-center px-4 sm:px-6">
        <div className="max-w-5xl w-full mx-auto">
          <div className="text-center space-y-6 sm:space-y-8">
            
            {/* Logo & Title Section */}
            <div className="flex flex-col items-center space-y-4 sm:space-y-5 animate-fade-in">
              {/* Logo */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full blur-2xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
                <div className="relative bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-3xl shadow-2xl border border-white/50">
                  <img 
                    src={logoKabupaten} 
                    alt="Logo Kabupaten Bogor" 
                    className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 object-contain"
                  />
                </div>
              </div>
              
              {/* Title */}
              <div className="space-y-2 sm:space-y-3">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800">
                  Desa Girimulya
                </h1>
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <div className="h-px w-12 sm:w-16 bg-gradient-to-r from-transparent via-teal-500 to-transparent"></div>
                  <p className="text-base sm:text-lg md:text-xl text-teal-700 font-medium">
                    Layanan Surat Digital
                  </p>
                  <div className="h-px w-12 sm:w-16 bg-gradient-to-r from-transparent via-teal-500 to-transparent"></div>
                </div>
              </div>
            </div>

            {/* Features Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-4xl mx-auto animate-slide-up">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="group bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-5 sm:p-6 hover:bg-white/90 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{feature.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            {deferredPrompt && (
              <div className="animate-fade-in-delay">
                <button
                  onClick={handleInstallClick}
                  className="group relative inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl sm:rounded-2xl font-semibold text-base sm:text-lg shadow-xl hover:shadow-2xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 hover:scale-105"
                >
                  <FiDownload className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span>Install Aplikasi</span>
                </button>
              </div>
            )}

            {/* Footer Badge */}
            <div className="flex items-center justify-center gap-2 text-gray-600 text-sm animate-fade-in-delay-2">
              <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
              <span>Layanan Resmi Pemerintah Desa</span>
            </div>
          </div>
        </div>
      </main>

      {/* Modern Install Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="relative bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-2xl max-w-md w-full p-8 animate-slide-up">
            {/* Close Button */}
            <button
              onClick={() => setShowInstallModal(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500 rounded-3xl blur-2xl opacity-30"></div>
                <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-3xl">
                  <img src={logoKabupaten} alt="Logo" className="w-16 h-16 object-contain" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-800 text-center mb-2">
              Install Aplikasi
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Surat Digital - Desa Girimulya
            </p>

            {/* Features */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-gray-700">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <FiCheck className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm">Akses cepat dari home screen</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <FiCheck className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm">Bekerja offline</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <FiCheck className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm">Notifikasi real-time</span>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2 font-semibold">📱 Cara Install:</p>
              <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                <li>Tap menu browser (⋮ atau ⋯)</li>
                <li>Pilih "Add to Home Screen" atau "Install"</li>
                <li>Tap "Add" atau "Install"</li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowInstallModal(false)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Nanti Saja
              </button>
              <button
                onClick={() => {
                  setShowInstallModal(false);
                  if (deferredPrompt) {
                    installPWA();
                  }
                }}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <FiDownload className="w-5 h-5" />
                Install
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;


